import React, { useState, useRef } from "react";
import "./styles.css"
import { Link } from "react-router-dom";

const ProductCard = ({ product, isClickable = true }) => {
  const [isHovered, setIsHovered] = useState(false);
  const touchStartTime = useRef(0);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const isTouchMoved = useRef(false);
  const longPressTimer = useRef(null);
  
  // Get dynamic sizes
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 640 : false;
  const cardSize = isMobile ? '120px' : '200px';

  // Handle touch start
  const handleTouchStart = (e) => {
    if (!isClickable) return;
    
    touchStartTime.current = Date.now();
    touchStartPos.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
    isTouchMoved.current = false;
    
    // Set hover state on touch
    setIsHovered(true);
    
    // Clear any existing long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    
    // Set long press timer (optional - for long press actions)
    longPressTimer.current = setTimeout(() => {
      // Long press action if needed
      console.log('Long press on:', product.name);
    }, 500);
  };

  // Handle touch move
  const handleTouchMove = (e) => {
    if (!isClickable) return;
    
    const touchX = e.touches[0].clientX;
    const touchY = e.touches[0].clientY;
    const deltaX = Math.abs(touchStartPos.current.x - touchX);
    const deltaY = Math.abs(touchStartPos.current.y - touchY);
    
    // If moved more than 10px, it's a scroll/swipe, not a click
    if (deltaX > 10 || deltaY > 10) {
      isTouchMoved.current = true;
      setIsHovered(false);
      
      // Clear long press timer
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    }
  };

  // Handle touch end
  const handleTouchEnd = (e) => {
    if (!isClickable) return;
    
    // Clear long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }
    
    const touchDuration = Date.now() - touchStartTime.current;
    
    // Only trigger click if:
    // 1. Touch didn't move (not a scroll/swipe)
    // 2. Touch duration is less than 300ms (quick tap)
    if (!isTouchMoved.current && touchDuration < 300) {
      e.preventDefault();
      
      // Add a small delay to ensure it's not part of a double-tap or scroll
      setTimeout(() => {
        handleProductClick();
      }, 50);
    }
    
    // Reset hover state
    setTimeout(() => {
      setIsHovered(false);
    }, 200);
  };

  // Handle mouse click
  const handleClick = (e) => {
    if (!isClickable) return;
    
    // Prevent click if it was a touch event (handled separately)
    if (e.nativeEvent.pointerType === 'touch') {
      return;
    }
    
    e.preventDefault();
    handleProductClick();
  };

  // Common click handler
  const handleProductClick = () => {
    if (!isClickable) return;
    
    if (product.website) {
      // Open in new tab
      window.open(product.website, '_blank', 'noopener,noreferrer');
    } else if (product.url) {
      // Use React Router navigation if it's an internal link
      window.open(product.url, '_blank', 'noopener,noreferrer');
    } else {
      console.log('No URL available for:', product.name);
    }
  };

  // Handle mouse enter - only if clickable
  const handleMouseEnter = () => {
    if (isClickable) {
      setIsHovered(true);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className={`relative z-10 overflow-hidden flex flex-col gap-2 flex-shrink-0 select-none group ${
        !isClickable ? 'cursor-default' : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={handleClick}
      role={isClickable ? "button" : "presentation"}
      tabIndex={isClickable ? 0 : -1}
      aria-label={isClickable ? `View ${product.name}` : undefined}
      style={{
        width: cardSize,
        height: cardSize,
        cursor: isClickable ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        pointerEvents: isClickable ? 'auto' : 'none',
        opacity: isClickable ? 1 : 0.7,
        transition: 'all 0.3s ease',
      }}
      // Handle keyboard navigation - only if clickable
      onKeyDown={(e) => {
        if (!isClickable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleProductClick();
        }
      }}
    >
      <div className={`relative overflow-hidden z-10 w-full h-full rounded-lg transition-all duration-300 ${
        isClickable ? 'group-hover:shadow-lg group-hover:shadow-blue-500/25' : ''
      }`}>
        {/* Colored version - shows on hover */}
        <img 
          src={product.logo} 
          alt={product.name || 'Product'} 
          className="absolute inset-0 object-contain w-full h-full transition-all duration-500 ease-in-out"
          draggable="false"
          style={{
            opacity: isHovered && isClickable ? 1 : 0,
            filter: 'none',
            transform: isHovered && isClickable ? 'scale(1.05)' : 'scale(1)',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="Arial" font-size="12" text-anchor="middle" x="50" y="55"%3ENo Image%3C/text%3E%3C/svg%3E';
          }}
        />
        
        {/* Grayscale version - shows by default, fades on hover */}
        <img 
          src={product.logo} 
          alt={product.name || 'Product'} 
          className="object-contain w-full h-full transition-all duration-500 ease-in-out"
          draggable="false"
          style={{
            filter: isClickable 
              ? (isHovered ? 'grayscale(0%)' : 'grayscale(0%)') 
              : 'grayscale(100%)',
            opacity: isHovered && isClickable ? 0 : 1,
            transform: isHovered && isClickable ? 'scale(1.05)' : 'scale(1)',
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
          onError={(e) => {
            e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext fill="%23999" font-family="Arial" font-size="12" text-anchor="middle" x="50" y="55"%3ENo Image%3C/text%3E%3C/svg%3E';
          }}
        />
        
        {/* Hover overlay with glow effect - only for clickable cards */}
        {isClickable && (
          <div 
            className={`absolute inset-0 transition-all duration-500 ease-in-out ${
              isHovered ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              pointerEvents: 'none',
            }}
          />
        )}
        
        {/* Non-clickable overlay */}
        {!isClickable && (
          <div 
            className="absolute inset-0 bg-gray-100 bg-opacity-20"
            style={{ pointerEvents: 'none' }}
          />
        )}
      </div>

    </div>
  );
};

export default ProductCard;